import { Alert, Badge, Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntityLabel, EntityMultiSelect } from '../../components/EntityField';
import { notifyDone, notifyError } from '../../lib/notify';

const GROUPS_PATH = '/admin/v1/catalog/modifier-groups';
const LINKS_PATH = '/admin/v1/catalog/item-modifier-groups';
const CONDITIONAL_LINKS_PATH = '/admin/v1/catalog/modifier-option-groups';

interface Link {
  id: string;
  menuItemId: string | null;
  variantId: string | null;
  modifierGroupId: string;
  displayOrder: number;
}

interface ConditionalLink {
  id: string;
  modifierOptionId: string;
  modifierGroupId: string;
  displayOrder: number;
  after: string;
}

/**
 * Which items ask this question, changed from the question.
 *
 * A set of choices reaches a guest only if it has answers AND is on something on the menu. Both
 * gaps were already visible here — the row says "No items" in orange — and neither could be fixed
 * from the row saying so. Putting "Cooked how?" on the six steaks meant opening each steak in turn
 * and picking the question out of a dropdown of every question on the menu: six screens to answer
 * something this row had already asked.
 *
 * The picker is the whole interaction, and it is a multi-select rather than an add-one-at-a-time
 * list because the real job arrives in a batch — a new question belongs to a handful of dishes at
 * once, and taking it off the one it was put on by mistake is the same gesture.
 */
export function QuestionItemsModal({
  group,
  onClose,
}: {
  group: Record<string, unknown> | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const groupId = group ? String(group.id) : '';
  const question = group ? String(group.nameEn ?? 'this question') : '';
  const noAnswers = Number(group?.optionCount ?? 0) === 0;

  const links = useQuery({
    queryKey: listKey(LINKS_PATH, { modifierGroupId: groupId }),
    queryFn: () => rawApi<Link[]>('GET', `${LINKS_PATH}?modifierGroupId=${groupId}`),
    enabled: Boolean(groupId),
  });
  const conditionalLinks = useQuery({
    queryKey: listKey(CONDITIONAL_LINKS_PATH, { modifierGroupId: groupId }),
    queryFn: () => rawApi<ConditionalLink[]>('GET', `${CONDITIONAL_LINKS_PATH}?modifierGroupId=${groupId}`),
    enabled: Boolean(groupId),
  });

  const attached = (links.data ?? []).filter((l) => l.menuItemId);
  const onSizes = (links.data ?? []).filter((l) => !l.menuItemId && l.variantId);

  const [picked, setPicked] = useState<string[] | null>(null);
  const [pickedAfter, setPickedAfter] = useState<string[] | null>(null);
  // Seeded from the server until the manager touches it, so reopening never shows a stale set.
  const chosen = picked ?? attached.map((l) => String(l.menuItemId));
  const chosenAfter = pickedAfter ?? (conditionalLinks.data ?? []).map((l) => l.modifierOptionId);

  const close = () => {
    setPicked(null);
    setPickedAfter(null);
    onClose();
  };

  const save = useMutation({
    mutationFn: async () => {
      const before = new Set(attached.map((l) => String(l.menuItemId)));
      const after = new Set(chosen);
      // Sequential on purpose: this is a handful of rows, and a burst of parallel writes against
      // D1 from a link with Roatán's latency is not worth the complexity of ordering the failures.
      for (const menuItemId of chosen.filter((id) => !before.has(id))) {
        await rawApi('POST', LINKS_PATH, { menuItemId, modifierGroupId: groupId, displayOrder: 0 });
      }
      for (const link of attached.filter((l) => !after.has(String(l.menuItemId)))) {
        await rawApi('DELETE', `${LINKS_PATH}/${link.id}`);
      }
      const beforeConditions = new Set((conditionalLinks.data ?? []).map((link) => link.modifierOptionId));
      const afterConditions = new Set(chosenAfter);
      for (const modifierOptionId of chosenAfter.filter((id) => !beforeConditions.has(id))) {
        await rawApi('POST', CONDITIONAL_LINKS_PATH, {
          modifierOptionId,
          modifierGroupId: groupId,
          displayOrder: 0,
        });
      }
      for (const link of (conditionalLinks.data ?? []).filter(
        (candidate) => !afterConditions.has(candidate.modifierOptionId),
      )) {
        await rawApi('DELETE', `${CONDITIONAL_LINKS_PATH}/${link.id}`);
      }
      return null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(LINKS_PATH, { modifierGroupId: groupId }) });
      // The Choices table counts the items asking each question, and every item's own setup screen
      // lists its questions — both are wrong the moment this saves.
      void queryClient.invalidateQueries({ queryKey: listKey(GROUPS_PATH) });
      void queryClient.invalidateQueries({ queryKey: ['list', LINKS_PATH] });
      void queryClient.invalidateQueries({ queryKey: ['list', CONDITIONAL_LINKS_PATH] });
      notifyDone(
        'Saved',
        chosen.length + chosenAfter.length === 0
          ? `Nothing asks "${question}" now, so the register will never bring it up.`
          : `The register will ask "${question}" in ${chosen.length + chosenAfter.length} configured place${chosen.length + chosenAfter.length === 1 ? '' : 's'} once you send the menu.`,
      );
      close();
    },
    onError: (err) => notifyError(err, 'change which items ask this'),
  });

  return (
    <Modal
      opened={Boolean(group)}
      onClose={close}
      title={question}
      size="lg"
      // The item picker's own list opens in a layer of its own, which counts as a click outside
      // this dialog: picking the third dish would otherwise close it and lose the first two.
      closeOnClickOutside={false}
    >
      {links.isError || conditionalLinks.isError ? (
        <Alert color="red" title="Could not load this">
          Which items ask this could not be reached. Nothing has been lost — this is only a display problem.
        </Alert>
      ) : links.isLoading || conditionalLinks.isLoading ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            The items the register asks this on. Staff see this question when they ring up any of them.
          </Text>

          {/* A question with no answers offers the guest nothing, so putting it on six dishes
              achieves nothing either — said here rather than left to be discovered at the register. */}
          {noAnswers && (
            <Alert color="orange" title="This question has no answers yet">
              Add its answers first — until it has some, the register has nothing to offer when it comes up.
            </Alert>
          )}

          <EntityMultiSelect
            entity="menuItem"
            label="Asked on"
            description="Pick every item this belongs to. Take one out to stop the register asking it there."
            value={chosen}
            onChange={setPicked}
          />

          <EntityMultiSelect
            entity="modifierOption"
            label="Asked only after"
            description="Use this for a follow-up: selecting Plantains asks Fried or Grilled; selecting another side does not."
            value={chosenAfter}
            onChange={setPickedAfter}
          />

          {(conditionalLinks.data ?? []).length > 0 && pickedAfter === null && (
            <Text size="xs" c="dimmed">
              Currently follows {(conditionalLinks.data ?? []).map((link) => link.after).join(', ')}.
            </Text>
          )}

          {onSizes.length > 0 && (
            <Text size="xs" c="dimmed">
              Also asked on {onSizes.length} particular {onSizes.length === 1 ? 'size' : 'sizes'}:{' '}
              {onSizes.map((l, i) => (
                <span key={l.id}>
                  {i > 0 && ', '}
                  <EntityLabel entity="variant" id={l.variantId} />
                </span>
              ))}
            </Text>
          )}

          {chosen.length === 0 && chosenAfter.length === 0 && !noAnswers && (
            <Badge color="orange" variant="light">
              On no items — the register will never ask it
            </Badge>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={close} disabled={save.isPending}>
              Cancel
            </Button>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
